import { observer, inject } from "mobx-react";
import React from "react";
import {
    ColumnDef,
    Column,
} from "@tanstack/react-table";

import { Utils } from "../../common/utils";

import { CommitteeStore } from "../../stores";
import { Committee } from "Stores/committeeStore";
import { AppStore } from "Stores/appStore";
import { Alert, Button, Form, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";

import { CommitteeFormModal } from "./formModal";
import { DashboardMappingSelector } from "./MappingSelector";
import { UsersFormModal } from "Components/Committees/usersFormModal";
import DebouncedInput from "Components/Common/DebouncedInput";
import { PlusIcon } from "Components/Icons/PlusIcon";
import { StyledTable } from "Components/Common/StyledTable";
import { EditIcon } from "Components/Icons/EditIcon";
import { DeleteIcon } from "Components/Icons/DeleteIcon";
import PageTitle from "Components/Common/PageTitle";

class DashCell extends React.Component<{ onClick: (committee: Committee) => void; committee: Committee }> {
    render() {
        return (
            <div className="dash-cell">
                <span>[{this.props.committee.dashboards?.length || 0}]</span>
                <button
                    id={`committee-edit-dashboard-button-${this.props.committee.id}`}
                    data-test-id={`committee-dashboard-edit-${this.props.committee.id}`}
                    onClick={() => this.props.onClick(this.props.committee)}>
                    {this.props.committee.protected && !Utils.isSuperAdmin()
                        ? <i className="icon icon-search" aria-hidden="true" />
                        : <EditIcon />}
                </button>

            </div>
        );
    }
}

interface ModifyCellProps {
    committee: Committee
    onShowModal: (committee: Committee) => void
    onDeleteCommittee: (committee: Committee) => void
}

class ModifyCell extends React.Component<ModifyCellProps> {

    handleEditClick = () => {
        this.props.onShowModal(this.props.committee);
    };

    handleDeleteClick = () => {
        this.props.onDeleteCommittee(this.props.committee);
    };

    render() {
        return (
            <div className="modify-cell">{this.props.committee.protected && !Utils.isSuperAdmin()
                ? <div style={{ cursor: "not-allowed" }}>
                    <OverlayTrigger
                        key={"tooltip-" + this.props.committee.id}
                        placement="top"
                        overlay={
                            <Tooltip>
                                Protected
                            </Tooltip>
                        }>
                        <button className={"btn-icon"} id={`committee-protected-button-${this.props.committee.id}`} disabled={true} style={{ marginRight: 3, fontSize: 24, marginTop: -3 }}>
                            <i className="icon icon-lock" aria-hidden="true" style={{ fontSize: 24, marginTop: 4, marginLeft: -7 }} />
                        </button>
                    </OverlayTrigger>
                </div>
                : <div className="container">
                    <Button
                        id={`committee-edit-button-${this.props.committee.id}`}
                        onClick={this.handleEditClick}
                        className={"btn-icon"}
                        style={{ padding: "0 5px 0 0", fontSize: 18 }}>
                        <EditIcon />
                    </Button>
                    <Button
                        id={`committee-delete-button-${this.props.committee.id}`}
                        onClick={this.handleDeleteClick}
                        className={"btn-icon"}
                        style={{ padding: "0 5px 0 0", fontSize: 18 }}>
                        <DeleteIcon className="delete-icon" />
                    </Button>
                </div>}
            </div>);
    }
}

interface CommitteeManagementContainerState {
    showCommitteeModal: boolean
    selectedCommittee?: Committee
    committees: Committee[]
    showModal?: boolean
    showUsersModal: boolean
    showMappingModal: boolean
    showFilters: boolean
    isUpdating: boolean
    searchValue: string
}

export interface CommitteeManagementProps {
    committeeStore?: CommitteeStore
    appStore?: AppStore
}

const UnauthorizedMessage = () => <div className="col-sm-12 col-md-12">
    <Alert variant="warning">
        {`Sorry, you don't have access to committee management. If you think this is an error,
    please contact your administrator so he/she can give you the correct access level.`}
    </Alert>
</div>;

@inject("committeeStore", "appStore")
@observer
export class CommitteeManagement extends React.Component<CommitteeManagementProps, CommitteeManagementContainerState> {
    state = {
        showCommitteeModal: false,
        selectedCommittee: undefined,
        committees: [] as Committee[],
        showModal: undefined,
        showUsersModal: false,
        showMappingModal: false,
        showFilters: false,
        isUpdating: false,
        searchValue: "",
    };

    columns: ColumnDef<Committee>[] = [
        {
            header: "Name",
            accessorKey: "name",
            size: 200,
        }, {
            header: "Dashboards",
            accessorFn: (committee) => committee.dashboards?.length || 0,
            cell: (props) => <DashCell committee={props.row.original} onClick={this.handleShowMappingClick} />,
        }, {
            header: "Users",
            accessorKey: "faculty",
            enableColumnFilter: false,
            cell: (props) => {
                return (
                    <div className="faculty-cell">
                        <span>[{props.row.original.faculty?.length || 0}]</span>
                        <button className="table-action-button inline" data-test-id={`committee-users-edit-${props.row.original.id}`} onClick={() => this.handleUsersShowModal(props.row.original)}>
                            <EditIcon />
                        </button>
                    </div>);
            }
        }, {
            header: "Modify",
            cell: (props) => <ModifyCell committee={props.row.original} onShowModal={this.handleShowModal} onDeleteCommittee={this.deleteCommittee} />
        }
    ];

    closeModal = () => {
        this.setState({ selectedCommittee: undefined, showCommitteeModal: false });
    };

    showModal = () => {
        this.setState({ showCommitteeModal: true, selectedCommittee: undefined });
    };

    handleSubmitForm = () => {
        this.setState({ selectedCommittee: undefined, showCommitteeModal: false, isUpdating: true }, () => {
            if (!this.props.committeeStore) {
                return;
            }
            Promise.resolve(this.props.committeeStore.fetchCommittees()).then((committees: Committee[]) => {
                let showFilters = false;
                if (committees.length > 20) {
                    showFilters = true;
                }
                this.setState({ isUpdating: false, committees, showFilters });
            });
        });
    };

    deleteCommittee = (committee: Committee) => {
        if (!this.props.appStore) {
            return;
        }

        this.props.appStore.showConfirmModal({
            element: (
                <span>{`Are you sure you want to delete ${committee.name}?`}</span>
            ),
            accept: () => {
                this.setState({ isUpdating: true }, () => {
                    if (!this.props.committeeStore) {
                        return;
                    }
                    Promise.resolve(this.props.committeeStore.deleteCommittee(committee.id)).then(() => {
                        if (!this.props.committeeStore) {
                            return;
                        }
                        this.props.committeeStore.fetchCommittees().then((committees: Committee[]) => {
                            let showFilters = false;
                            if (committees.length > 20) {
                                showFilters = true;
                            }
                            this.setState({ isUpdating: false, committees, showFilters });
                        });
                    });
                });
            },
            cancel: () => { },
            showActions: true
        });
    };

    handleUpdateCommittee = (committeeId: number, callback: () => void) => {
        if (!this.props.committeeStore) {
            return;
        }
        this.props.committeeStore.fetchCommittee(committeeId).then(() => {
            if (!this.props.committeeStore) {
                return;
            }
            this.setState({ selectedCommittee: this.props.committeeStore.committee }, () => {
                callback();
            });
        });

        this.setState({ isUpdating: true }, () => {
            if (!this.props.committeeStore) {
                return;
            }
            this.props.committeeStore.fetchCommittees().then((committees: Committee[]) => {
                this.setState({ isUpdating: false, committees });
            });
        });
    };

    showMap = (committee: Committee) => {
        if (!this.props.committeeStore) {
            return;
        }
        this.props.committeeStore.fetchAllWorkbooks();
        this.setState({ selectedCommittee: committee, showMappingModal: true });
    };

    hideMap = () => {
        this.setState({ showMappingModal: false, selectedCommittee: undefined, showUsersModal: false });
    };

    componentDidMount() {
        Utils.setViewName("CommitteeList", "Committees");
        if (Utils.hasAdminAccess()) {
            if (!this.props.committeeStore) {
                return;
            }
            this.props.committeeStore.fetchCommittees().then((committees: Committee[]) => {
                this.setState({ committees, showFilters: true });
            });
        }
    }

    handleShowMappingClick = (committee: Committee) => {
        this.showMap(committee);
    };

    toggleDisplayFilters = () => {
        this.setState({ showFilters: !this.state.showFilters });
    };

    handleShowModal = (committee: Committee) => {
        this.setState({ showCommitteeModal: true, selectedCommittee: committee });
    };

    handleUsersShowModal = (committee: Committee) => {
        this.setState({ showUsersModal: true, selectedCommittee: committee });
    };

    handleUpdateUsersForCommittee = () => {
        if (!this.props.committeeStore) {
            return;
        }
        this.setState({ isUpdating: true }, () => {
            if (!this.props.committeeStore) {
                return;
            }
            this.props.committeeStore.fetchCommittees().then((committees: Committee[]) => {
                this.setState({ isUpdating: false, committees });
            });
        });
        this.hideMap();
    };

    render() {
        return (
            <div className="card-layout">
                <PageTitle pageTitle={"Committees"} />
                {Utils.hasAdminAccess() ?
                    <React.Fragment>
                        <div className="title">
                            <h3>Committees</h3>

                            <div>
                                <Button id="committees-create-committee-button" data-test-id="committees-create" variant="primary" className="d-flex align-items-center new-course-btn gap-2 text-nowrap" onClick={this.showModal}>
                                    <PlusIcon className="white d-flex" />
                                    Create Committees
                                </Button>
                            </div>
                        </div>

                        <div className="search-bar">
                            <DebouncedInput
                                type="text"
                                value={this.state.searchValue}
                                onChange={(value) => this.setState({ searchValue: value as string})}
                                placeholder="Search by name or dashboard name"
                                className="search-input"
                                data-test-id="committees-search"
                            />
                            <i className={"type icon icon-search search-icon-bar"} aria-hidden="true" data-testid="icon-search"></i>
                        </div>

                        {this.props.committeeStore?.hasCaughtError && (
                            <div style={{ textAlign: "center", paddingTop: 80 }}>
                                {"Something went wrong, please try again"}
                            </div>
                        )}

                        {(!this.props.committeeStore?.committees || this.state.isUpdating) && !this.props.committeeStore?.hasCaughtError && (
                            <div style={{ textAlign: "center", paddingTop: 80 }}>
                                <Spinner animation="border" variant="secondary" />
                            </div>
                        )}

                        {this.props.committeeStore?.committees && this.state.committees.length > 0 && (
                            <div className="list users">
                                <CommitteeListReactTable columns={this.columns} defaultData={this.state.committees} searchValue={this.state.searchValue} showFilters={this.state.showFilters} />
                            </div>
                        )}

                        {this.props.committeeStore?.committees && this.state.committees.length === 0 && (
                            <Alert variant="warning">
                                {"No committees available"}
                            </Alert>
                        )}

                    </React.Fragment>
                    : <UnauthorizedMessage />
                }

                {this.state.showMappingModal &&
                    <DashboardMappingSelector
                        handleClose={this.hideMap}
                        committee={this.state.selectedCommittee}
                        updateCommittee={this.handleUpdateCommittee} />
                }

                {this.state.showCommitteeModal &&
                    <CommitteeFormModal
                        selectedCommittee={this.state.selectedCommittee}
                        handleCloseModal={this.closeModal}
                        handleSubmitCommittee={this.handleSubmitForm} />
                }

                {this.state.showUsersModal
                    && <UsersFormModal
                        committee={this.state.selectedCommittee}
                        handleClose={this.hideMap}
                        handleSubmit={this.handleUpdateUsersForCommittee} />}
            </div>
        );
    }
}

interface CommitteeListReactTableProps {
    columns: ColumnDef<Committee>[]
    defaultData: Committee[]
    searchValue: string
    showFilters: boolean
}
function CommitteeListReactTable({ columns, defaultData, searchValue }: CommitteeListReactTableProps) {

    const filteredData = React.useMemo(() => {
        if (!searchValue) return defaultData;
        const lowerSearchValue = searchValue.toLowerCase();
        return defaultData.filter((committee) => {
            const nameMatches = committee.name.toLowerCase().includes(lowerSearchValue);
            const dashboardMatches =
                committee.dashboards &&
                committee.dashboards.some((dashboard) =>
                    dashboard.name.toLowerCase().includes(lowerSearchValue)
                );
            return nameMatches || dashboardMatches;
        });
    }, [defaultData, searchValue]);

    return (
        <StyledTable
            defaultData={filteredData}
            columns={columns}
            className="committees-table"
        />
    );
}

export function Filter({ column }: { column: Column<any, unknown> }) {
    const columnFilterValue = column.getFilterValue();
    const onChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        column.setFilterValue(() => e.target.value);
    };

    return column.id === "protected" ? (
        <Form.Select aria-label="All" onChange={onChange}>
            <option value="all">All</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
        </Form.Select>) : (
        <DebouncedInput
            type="text"
            value={(columnFilterValue ?? "") as string}
            onChange={value => column.setFilterValue(value)}
        />);
}